import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons';
import { faCircleInfo, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@parentsquare/squarekit/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@parentsquare/squarekit/components/ui/tooltip';
import { cn } from '@parentsquare/squarekit/lib/utils';
import { cva } from 'class-variance-authority';
import React from 'react';

/**
 * Component version. `v1` follows the SquareKit v1 token system
 * (`--color-text-on-surface-*`, `--color-surface-base`, `--font-size-*`,
 * `--radius-4`, etc.) and matches the Figma `Page Header` spec (Type=Page and
 * Type=School). `v0` keeps the legacy hardcoded-hex render so existing callers
 * are byte-identical. Defaults to `v1`. Pass `variant="psq"` or `variant="classic"`
 * (or `version="v0"`) to keep the legacy render.
 */
export type PageHeaderVersion = 'v0' | 'v1';

/**
 * v1 header type. `page` is the standard page header (back arrow, title +
 * optional info tooltip, supporting text, action slot). `school` renders a
 * school identity header (logo, school name as an h2, tagline, action slot).
 * Ignored in v0.
 */
export type PageHeaderType = 'page' | 'school';

type PageHeaderHeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;

const pageHeaderVariants = cva(``, {
  variants: {
    variant: {
      psq: 'tw:flex tw:pt-3 tw:pb-3 tw:items-center tw:text-[#333]',
      classic:
        'tw:flex tw:flex-col tw:gap-3 tw:p-3 tw:bg-white tw:sm:flex-row tw:sm:items-center tw:sm:justify-between tw:sm:gap-0',
    },
  },
  defaultVariants: {
    variant: 'psq',
  },
});

type PageHeaderHtmlAttributes = Omit<React.HTMLAttributes<HTMLDivElement>, 'children'>;

interface PageHeaderCoreProps {
  children: React.ReactNode;
  icon?: React.ReactNode;
  actionButton?: React.ReactNode;
}

type PageHeaderPsqProps = PageHeaderCoreProps & {
  version?: 'v0';
  variant?: 'psq' | undefined;
  subtitle?: never;
  showCloseButton?: never;
  onClose?: never;
  closeButtonAriaLabel?: never;
};

type PageHeaderClassicNoCloseProps = PageHeaderCoreProps & {
  version?: 'v0';
  variant: 'classic';
  subtitle?: React.ReactNode;
  showCloseButton?: false | undefined;
  onClose?: never;
  closeButtonAriaLabel?: never;
};

type PageHeaderClassicWithCloseProps = PageHeaderCoreProps & {
  version?: 'v0';
  variant: 'classic';
  subtitle?: React.ReactNode;
  showCloseButton: true;
  onClose: () => void;
  closeButtonAriaLabel: string;
};

type PageHeaderV0Props = PageHeaderHtmlAttributes &
  (PageHeaderPsqProps | PageHeaderClassicNoCloseProps | PageHeaderClassicWithCloseProps);

type PageHeaderV1PageBaseProps = {
  version?: 'v1';
  type?: 'page';
  /** Title text rendered as the semantic heading (see `headingLevel`). */
  children: React.ReactNode;
  /**
   * Semantic heading level for the title. Defaults to `1` (the page heading).
   * The visual styling is fixed to the Figma h3 spec regardless of level.
   * @default 1
   */
  headingLevel?: PageHeaderHeadingLevel;
  /**
   * Optional supporting/help text rendered below the title.
   */
  subtitle?: React.ReactNode;
  /**
   * Shows the built-in info tooltip: a solid circle-info icon that reveals
   * `infoTooltipContent` on hover/focus. The icon is the only trigger and is
   * owned by the component (consumers cannot pass a custom node).
   * @default false
   */
  showInfoTooltip?: boolean;
  /**
   * Where the info tooltip renders. `title` (default) places the icon inline
   * after the title; `trailing` places it in the trailing cluster before the
   * close / action control, separated by a vertical divider (Figma "New Post"
   * header). Ignored when `showInfoTooltip` is false.
   * @default 'title'
   */
  infoTooltipPlacement?: 'title' | 'trailing';
  /**
   * The tooltip body shown on hover/focus of the info icon. Required for the
   * tooltip to convey anything; ignored when `showInfoTooltip` is false.
   */
  infoTooltipContent?: React.ReactNode;
  /**
   * Accessible label for the info icon trigger button. Falls back to
   * `infoTooltipContent` when it is a string, otherwise `'More information'`.
   */
  infoTooltipAriaLabel?: string;
  /**
   * Optional trailing action slot (a `<Button>`, icon button, combobox, etc.)
   * rendered on the trailing edge. Takes precedence over the built-in close
   * button.
   */
  actionButton?: React.ReactNode;
  /**
   * When true and a trailing control is present (`actionButton`, built-in close,
   * or trailing info tooltip), stack the trailing cluster below the title block
   * on mobile and keep a single horizontal row from the `sm` breakpoint up.
   * Matches v0 `classic` responsive behavior. Ignored when there is no
   * trailing control.
   * @default true
   */
  stackOnMobile?: boolean;
};

/**
 * Back-navigation arrow props. Modeled as a discriminated union so that when
 * `showBackButton` is `true`, `onBack` and `backButtonAriaLabel` are required:
 * the rendered `<Button>` needs a click handler and an accessible name.
 */
type PageHeaderV1BackProps =
  | { showBackButton?: false; onBack?: never; backButtonAriaLabel?: never }
  | { showBackButton: true; onBack: () => void; backButtonAriaLabel: string };

/**
 * Built-in close button props. Modeled as a discriminated union so that when
 * `showCloseButton` is `true`, `onClose` and `closeButtonAriaLabel` are
 * required. Ignored when `actionButton` is provided.
 */
type PageHeaderV1CloseProps =
  | { showCloseButton?: false; onClose?: never; closeButtonAriaLabel?: never }
  | { showCloseButton: true; onClose: () => void; closeButtonAriaLabel: string };

type PageHeaderV1PageProps = PageHeaderHtmlAttributes &
  PageHeaderV1PageBaseProps &
  PageHeaderV1BackProps &
  PageHeaderV1CloseProps;

type PageHeaderV1SchoolProps = PageHeaderHtmlAttributes & {
  version?: 'v1';
  type: 'school';
  /** School name rendered as the heading (h2 styling, see `headingLevel`). */
  children: React.ReactNode;
  /**
   * Semantic heading level for the school name. Defaults to `1`.
   * @default 1
   */
  headingLevel?: PageHeaderHeadingLevel;
  /** Optional tagline rendered below the school name. */
  subtitle?: React.ReactNode;
  /**
   * Optional leading logo node (e.g. an `<img>` or `<svg>`). The wrapper fixes
   * the rendered media to 67px wide and lets the height scale with the logo's
   * aspect ratio; the consumer owns its accessibility.
   */
  logo?: React.ReactNode;
  /** Optional trailing action slot (e.g. a district/groups combobox). */
  actionButton?: React.ReactNode;
  /**
   * When true and `actionButton` is set, stack the action below the school
   * identity block on mobile. See `stackOnMobile` on the page type.
   * @default true
   */
  stackOnMobile?: boolean;
};

export type PageHeaderProps = PageHeaderV0Props | PageHeaderV1PageProps | PageHeaderV1SchoolProps;

// Figma `Page Header` spec: 16px horizontal padding, 8px vertical padding,
// 12px gap between back arrow / title block / action slot. Items are centered
// on the 44px content row (title line-height 24 + help line-height 20).
const V1_ROOT_BASE_CLASSES =
  'tw:flex tw:w-full tw:gap-(--spacing-12) tw:px-(--spacing-16) tw:py-(--spacing-8) tw:bg-(--color-surface-base)';

const V1_ROOT_ROW_CLASSES = 'tw:min-h-[60px] tw:items-center';

const V1_ROOT_STACKED_CLASSES =
  'tw:flex-col tw:items-stretch tw:min-h-0 tw:sm:flex-row tw:sm:items-center tw:sm:min-h-[60px]';

const V1_LEADING_CLUSTER_CLASSES =
  'tw:flex tw:min-w-0 tw:w-full tw:items-center tw:gap-(--spacing-12) tw:sm:min-w-0 tw:sm:flex-auto';

const v1RootLayoutClasses = (stackOnMobile: boolean, hasTrailing: boolean) =>
  cn(
    V1_ROOT_BASE_CLASSES,
    stackOnMobile && hasTrailing ? V1_ROOT_STACKED_CLASSES : V1_ROOT_ROW_CLASSES
  );

// h3 token: 18px / 24 line-height / -0.12% letter-spacing (-0.0216px at 18px).
const V1_TITLE_CLASSES =
  'tw:m-0 tw:text-(length:--font-size-lg) tw:font-(--font-weight-semibold) tw:leading-(--line-height-24px) tw:tracking-[-0.0216px] tw:text-(--color-text-on-surface-primary)';

// h2 token: 24px / 30 line-height / -0.12% letter-spacing (-0.0288px at 24px).
const V1_SCHOOL_NAME_CLASSES =
  'tw:m-0 tw:text-(length:--font-size-2xl) tw:font-(--font-weight-semibold) tw:leading-(--line-height-30px) tw:tracking-[-0.0288px] tw:text-(--color-text-on-surface-primary)';

// body/small token: 14px / 20 line-height / 0.25% letter-spacing (0.035px at 14px).
const V1_HELP_CLASSES =
  'tw:text-(length:--font-size-sm) tw:font-(--font-weight-regular) tw:leading-(--line-height-20px) tw:tracking-[0.035px] tw:text-(--color-text-on-surface-secondary)';

// Info icon trigger: 16px solid circle-info (dark filled circle, knockout "i")
// per Figma. The svg inherits --color-icon-on-surface-primary.
const V1_INFO_TOOLTIP_TRIGGER_CLASSES =
  'tw:flex tw:shrink-0 tw:cursor-pointer tw:items-center tw:rounded-(--radius-4) tw:text-(--color-icon-on-surface-primary) tw:focus-visible:outline-hidden tw:focus-visible:shadow-[var(--shadow-state-focus)] tw:[&_svg]:h-4 tw:[&_svg]:w-4';

// Vertical divider between a trailing info tooltip and the close/action control.
// Figma spec: 12px tall, 1px wide, --color-border-base.
const V1_TRAILING_DIVIDER_CLASSES = 'tw:h-3 tw:w-px tw:shrink-0 tw:bg-(--color-border-base)';

const PageHeaderInfoTooltip = ({
  content,
  ariaLabel,
}: {
  content: React.ReactNode;
  ariaLabel: string;
}) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <button type="button" aria-label={ariaLabel} className={V1_INFO_TOOLTIP_TRIGGER_CLASSES}>
          <FontAwesomeIcon icon={faCircleInfo} aria-hidden="true" className="tw:h-4 tw:w-4" />
        </button>
      </TooltipTrigger>
      <TooltipContent>{content}</TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

// School logos vary in shape, so the wrapper fixes the rendered width to 67px
// (Figma school-header spec) and lets the height scale with the logo's aspect
// ratio.
const V1_LOGO_CLASSES =
  'tw:flex tw:shrink-0 tw:items-center tw:justify-center tw:[&_img]:w-[67px] tw:[&_img]:h-auto tw:[&_img]:object-contain tw:[&_svg]:w-[67px] tw:[&_svg]:h-auto';

// v0 callers identify themselves via `variant` (`psq` | `classic`); v1 props omit
// that field entirely (see PageHeaderV0Props vs PageHeaderV1*Props). Checking
// `variant` therefore routes legacy callers even if a future caller passed both
// keys at runtime — TypeScript's union prevents that in typed code.
function isPageHeaderV0(props: PageHeaderProps): props is PageHeaderV0Props {
  return props.version === 'v0' || ('variant' in props && props.variant !== undefined);
}

const PageHeader = (props: PageHeaderProps) => {
  if (!isPageHeaderV0(props)) {
    if (props.type === 'school') {
      return <PageHeaderV1School {...props} />;
    }
    return <PageHeaderV1Page {...props} />;
  }

  return <PageHeaderV0 {...props} />;
};

const PageHeaderV0 = (props: PageHeaderV0Props) => {
  const {
    children,
    icon,
    className,
    subtitle,
    showCloseButton,
    onClose,
    closeButtonAriaLabel,
    actionButton,
    version: _version,
    ...htmlProps
  } = props;

  if (props.variant === 'classic') {
    return (
      <div className={cn(pageHeaderVariants({ variant: 'classic' }), className)} {...htmlProps}>
        <div className="tw:flex tw:min-w-0 tw:items-center tw:gap-3">
          {icon && <div className="tw:flex tw:shrink-0 tw:items-center">{icon}</div>}
          <div className="tw:min-w-0 tw:flex-auto">
            <h1 className="tw:text-lg tw:font-bold tw:text-[#191919] tw:antialiased">{children}</h1>
            {subtitle && <p className="tw:text-sm tw:text-[#4D4D4D]">{subtitle}</p>}
          </div>
        </div>
        {(actionButton || showCloseButton === true) && (
          <div className="tw:shrink-0">
            {actionButton ||
              (showCloseButton === true && (
                // similar to close button in dialog.tsx
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onClose}
                  aria-label={closeButtonAriaLabel}
                >
                  <FontAwesomeIcon
                    icon={faXmark}
                    size="sm"
                    className="tw:h-4 tw:w-4 tw:text-interaction-primaryButton tw:hover:text-interaction-primaryButton-hover tw:active:text-interaction-primaryButton-active"
                  />
                </Button>
              ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <hgroup
      className={cn(pageHeaderVariants({ variant: props.variant }), className)}
      {...htmlProps}
    >
      {icon}
      <h1 className="tw:text-xl tw:font-bold">{children}</h1>
    </hgroup>
  );
};

const PageHeaderV1Page = (props: PageHeaderV1PageProps) => {
  const {
    children,
    className,
    headingLevel = 1,
    subtitle,
    showInfoTooltip,
    infoTooltipPlacement = 'title',
    infoTooltipContent,
    infoTooltipAriaLabel,
    showBackButton,
    onBack,
    backButtonAriaLabel,
    actionButton,
    showCloseButton,
    onClose,
    closeButtonAriaLabel,
    stackOnMobile = true,
    version: _version,
    type: _type,
    ...htmlProps
  } = props;

  const HeadingTag = `h${headingLevel}` as const;
  const showTrailingTooltip = !!showInfoTooltip && infoTooltipPlacement === 'trailing';
  const showTitleTooltip = !!showInfoTooltip && !showTrailingTooltip;
  const infoTooltipLabel =
    infoTooltipAriaLabel ??
    (typeof infoTooltipContent === 'string' ? infoTooltipContent : 'More information');
  const infoTooltipNode = (
    <PageHeaderInfoTooltip content={infoTooltipContent} ariaLabel={infoTooltipLabel} />
  );

  const trailingControl =
    actionButton ??
    (showCloseButton ? (
      <Button
        variant="ghost"
        size="icon"
        version="v1"
        scale="sm"
        onClick={onClose}
        aria-label={closeButtonAriaLabel}
      >
        <FontAwesomeIcon icon={faXmark} aria-hidden="true" className="tw:h-3 tw:w-3" />
      </Button>
    ) : null);

  const hasTrailing = showTrailingTooltip || !!trailingControl;
  const useStackedLayout = stackOnMobile && hasTrailing;

  const backButton = showBackButton ? (
    <Button
      variant="ghost"
      size="icon"
      version="v1"
      scale="sm"
      onClick={onBack}
      aria-label={backButtonAriaLabel}
      // Figma spec: 16px from the arrow glyph edge to the title. The ghost
      // icon button adds ~6px optical space around its 12px glyph, and the
      // root flex gap adds 12px, so a -2px margin lands the glyph→text
      // distance at ~16px (6px optical + 12px gap - 2px = 16px).
      className="tw:mr-[calc(var(--spacing-2)*-1)] tw:shrink-0"
    >
      <FontAwesomeIcon icon={faArrowLeft} aria-hidden="true" className="tw:h-3 tw:w-3" />
    </Button>
  ) : null;

  const titleBlock = (
    <div className="tw:flex tw:min-w-0 tw:flex-auto tw:flex-col tw:justify-center">
      <div className="tw:flex tw:min-w-0 tw:items-center tw:gap-(--spacing-8)">
        <HeadingTag className={cn(V1_TITLE_CLASSES, 'tw:min-w-0 tw:truncate')}>
          {children}
        </HeadingTag>
        {showTitleTooltip && infoTooltipNode}
      </div>
      {subtitle && <p className={V1_HELP_CLASSES}>{subtitle}</p>}
    </div>
  );

  const trailingCluster = hasTrailing ? (
    <div className="tw:flex tw:shrink-0 tw:items-center tw:gap-(--spacing-8)">
      {showTrailingTooltip && infoTooltipNode}
      {showTrailingTooltip && trailingControl && (
        <span aria-hidden="true" className={V1_TRAILING_DIVIDER_CLASSES} />
      )}
      {trailingControl}
    </div>
  ) : null;

  if (useStackedLayout) {
    return (
      <div
        className={cn(v1RootLayoutClasses(stackOnMobile, hasTrailing), className)}
        {...htmlProps}
      >
        <div className={V1_LEADING_CLUSTER_CLASSES}>
          {backButton}
          {titleBlock}
        </div>
        {trailingCluster}
      </div>
    );
  }

  return (
    <div className={cn(v1RootLayoutClasses(stackOnMobile, hasTrailing), className)} {...htmlProps}>
      {backButton}
      {titleBlock}
      {trailingCluster}
    </div>
  );
};

const PageHeaderV1School = (props: PageHeaderV1SchoolProps) => {
  const {
    children,
    className,
    headingLevel = 1,
    subtitle,
    logo,
    actionButton,
    stackOnMobile = true,
    version: _version,
    type: _type,
    ...htmlProps
  } = props;

  const HeadingTag = `h${headingLevel}` as const;
  const hasTrailing = !!actionButton;
  const useStackedLayout = stackOnMobile && hasTrailing;

  const identityBlock = (
    <>
      {logo && <span className={V1_LOGO_CLASSES}>{logo}</span>}
      <div className="tw:flex tw:min-w-0 tw:flex-auto tw:flex-col tw:justify-center">
        <HeadingTag className={cn(V1_SCHOOL_NAME_CLASSES, 'tw:min-w-0 tw:truncate')}>
          {children}
        </HeadingTag>
        {subtitle && <p className={V1_HELP_CLASSES}>{subtitle}</p>}
      </div>
    </>
  );

  const trailingCluster = actionButton ? (
    <div className="tw:flex tw:shrink-0 tw:items-center">{actionButton}</div>
  ) : null;

  if (useStackedLayout) {
    return (
      <div
        className={cn(v1RootLayoutClasses(stackOnMobile, hasTrailing), className)}
        {...htmlProps}
      >
        <div className={V1_LEADING_CLUSTER_CLASSES}>{identityBlock}</div>
        {trailingCluster}
      </div>
    );
  }

  return (
    <div className={cn(v1RootLayoutClasses(stackOnMobile, hasTrailing), className)} {...htmlProps}>
      {identityBlock}
      {trailingCluster}
    </div>
  );
};

export { PageHeader };
